import { forwardRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, Sparkles, Smartphone, Lock, BookOpen, MapPin, MessageCircle, Zap, CreditCard, Timer } from "lucide-react";
import { extractVersions, newOrderRef, fetchPickupPoints, fetchPayConfig, discountLabel } from "../../lib/api";
import { OnlinePayDialog } from "./OnlinePayDialog";
import { formatLeft } from "./useLaunchOffer";
import { PaperOrderDialog } from "./PaperOrderDialog";
import { OrderLink } from "./OrderLink";

const btnCls = "flex items-center justify-start gap-4 bg-[#2C402E] hover:bg-[#1F2D21] text-[#FAF9F6] font-body tracking-wide transition-colors w-full px-6 py-4 text-left";
const outlineCls = "flex items-center justify-center gap-2 border py-3.5 font-body text-sm tracking-wide transition-colors w-full px-4";

const SmartLink = ({ url, onClick, testid, className, children }) =>
  url.startsWith("/") ? (
    <Link to={url} onClick={onClick} data-testid={testid} className={className}>{children}</Link>
  ) : (
    <a href={url} target="_blank" rel="noreferrer" onClick={onClick} data-testid={testid} className={className}>{children}</a>
  );

const OptionLabel = ({ n, children }) => (
  <p className="border-l-2 border-[#9E4735] pl-3 text-[10px] leading-relaxed tracking-[0.2em] uppercase text-[#9E4735] font-body mb-3">
    {n ? <strong className="font-semibold">Option {n} · </strong> : null}{children}
  </p>
);

const PickupPointsList = ({ points }) => (
  <div data-testid="cta-pickup-summary" className="mt-4 font-body">
    <p className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A] mb-2"><MapPin size={12} className="text-[#9E4735]" /> Retrait disponible chez</p>
    <ul className="grid grid-cols-2 gap-2">
      {points.map((p) => (
        <li key={p.id} data-testid={`cta-pickup-${p.id}`} className="border border-[#D1CFC7] bg-[#FAF9F6] px-3 py-2.5 flex flex-col items-center justify-center text-center min-h-[84px]">
          {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-9 max-w-full object-contain mb-1.5" loading="lazy" /> : null}
          <span className="text-xs font-semibold text-[#1A1A1A] leading-snug break-words">{p.name}</span>
          <span className="text-[10px] text-[#757575] mt-0.5">{p.city}</span>
          {p.kind === "pick_read" && <span data-testid={`cta-pickup-pickread-${p.id}`} className="mt-1.5 px-1.5 py-0.5 bg-[#2C402E] text-[#FAF9F6] text-[9px] tracking-[0.15em] uppercase">Relais Pick &amp; Read</span>}
        </li>
      ))}
    </ul>
  </div>
);

const OfferBanner = ({ offer, base, promo }) => (
  <div data-testid="launch-offer-banner" className="border border-[#9E4735] bg-[#9E4735]/5 px-4 py-3 mb-3 font-body">
    <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735] font-semibold">Offre de lancement · -{offer.percent} %</p>
    <p className="mt-1 text-sm text-[#1A1A1A]"><s data-testid="launch-offer-old-price" className="text-[#757575]">{base}</s> <strong data-testid="launch-offer-new-price" className="ml-1">{promo}</strong></p>
    <p data-testid="launch-offer-countdown" className="flex items-center gap-1.5 mt-1 text-xs text-[#4A4A4A]"><Timer size={12} className="text-[#9E4735]" /> Encore {formatLeft(offer.left)} pour en profiter</p>
  </div>
);

const DataSaverBlock = () => (
  <div data-testid="extract-data-saver" className="max-w-md mx-auto mt-10 border border-[#2C402E]/30 bg-[#F4F1EA] px-5 py-4 font-body">
    <p className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#2C402E] font-semibold mb-3"><Zap size={14} className="text-[#9E4735]" /> Lecture ultra-économique en données</p>
    <ul className="space-y-1.5 text-sm text-[#1A1A1A] list-disc pl-5 marker:text-[#9E4735]">
      <li>Consomme moins de 1 Mo (moins qu'une photo WhatsApp)</li>
      <li>Lisible même en coupant votre connexion internet (hors-ligne)</li>
      <li>Zéro application à installer</li>
      <li>Déblocage instantané sur votre écran</li>
    </ul>
  </div>
);

export const ExtractCTA = forwardRef(({ extract, sponsor, lieu, onTrack, offer }, ref) => {
  const versions = extractVersions(extract);
  const baseDigital = versions.find((v) => v.key === "numerique");
  const digital = baseDigital && offer ? { ...baseDigital, price: discountLabel(baseDigital.price, offer.percent) } : baseDigital;
  const [payOpen, setPayOpen] = useState(false);
  const [canPay, setCanPay] = useState(false);
  useEffect(() => { if (baseDigital) fetchPayConfig(extract.slug).then((c) => setCanPay(c.enabled)).catch(() => {}); }, [extract.slug, baseDigital]);
  const paper = versions.find((v) => v.key === "papier");
  const legacy = versions.find((v) => v.key === "livre");
  const refs = useMemo(() => ({ numerique: newOrderRef(), papier: newOrderRef(), livre: newOrderRef() }), []);
  const [points, setPoints] = useState([]);
  const [dialog, setDialog] = useState(false);
  const linkedIds = (extract.pickup_ids || []).join(",");
  useEffect(() => {
    if (!paper) return;
    const ids = linkedIds ? linkedIds.split(",") : null;
    fetchPickupPoints().then((all) => setPoints(ids ? all.filter((p) => ids.includes(p.id)) : [])).catch(() => setPoints([]));
  }, [paper, linkedIds]);
  const numbered = digital && paper;
  const common = { extract, sponsor, lieu, onTrack };

  return (
    <section ref={ref} data-testid="extract-cta" className="mt-20 border-t border-[#D1CFC7] pt-12 scroll-mt-20">
      <p data-testid="extract-end-marker" className="text-center text-[10px] tracking-[0.3em] uppercase text-[#9E4735] font-body mb-4">Fin de l'extrait</p>
      <h2 className="flex items-center justify-center gap-3 font-serif text-3xl sm:text-4xl text-[#1A1A1A] font-light leading-tight mb-10 text-center">
        <Sparkles size={22} className="text-[#9E4735] flex-shrink-0" /> Cet extrait vous a plu&nbsp;?
      </h2>
      {extract.whatsapp_number && (
        <div className="space-y-10 max-w-md mx-auto">
          {digital && (
            <div data-testid="cta-option-digital">
              <OptionLabel n={numbered && 1}>Lecture immédiate sur mobile (partout dans le monde)</OptionLabel>
              {offer && <OfferBanner offer={offer} base={baseDigital.price} promo={digital.price} />}
              {canPay && (
                <button onClick={() => setPayOpen(true)} data-testid="extract-online-pay-cta" className={`${btnCls} mb-3`}>
                  <CreditCard size={20} className="flex-shrink-0" />
                  <span className="flex flex-col leading-tight"><span className="text-sm sm:text-base uppercase tracking-wider">Payer et lire maintenant</span><span className="text-xs text-[#FAF9F6]/75 mt-1">{digital.price} · Orange Money, MTN MoMo, carte</span></span>
                </button>
              )}
              <OrderLink {...common} version={digital} orderRef={refs.numerique} offerToken={offer?.token} testid="extract-whatsapp-cta-numerique" className={canPay ? `${outlineCls} border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-[#FAF9F6]` : btnCls}>
                {canPay ? <><MessageCircle size={16} /> Ou commander via WhatsApp</> : <>
                <Smartphone size={20} className="flex-shrink-0" />
                <span className="flex flex-col leading-tight"><span className="text-sm sm:text-base uppercase tracking-wider">Continuer la lecture sur mon téléphone</span><span className="text-xs text-[#FAF9F6]/75 mt-1">{digital.price}</span></span>
                </>}
              </OrderLink>
              {canPay && <OnlinePayDialog open={payOpen} onOpenChange={setPayOpen} extract={extract} price={digital.price} orderRef={refs.numerique} offerToken={offer?.token} lieu={lieu} onTrack={onTrack} />}
              <p className="flex items-center justify-center gap-2 text-xs font-body text-[#4A4A4A] mt-3"><Lock size={12} className="text-[#9E4735]" /> Déblocage en quelques minutes sur votre écran • Zéro application requise</p>
            </div>
          )}
          {paper && (
            <div data-testid="cta-option-paper">
              <OptionLabel n={numbered && 2}>Recevoir ou retirer le livre papier</OptionLabel>
              <button onClick={() => setDialog(true)} data-testid="extract-whatsapp-cta-papier" className={btnCls}>
                <BookOpen size={20} className="flex-shrink-0" />
                <span className="flex flex-col leading-tight"><span className="text-sm sm:text-base uppercase tracking-wider">Commander le livre papier</span><span className="text-xs text-[#FAF9F6]/75 mt-1">{paper.price}</span></span>
              </button>
              {points.length > 0 && <PickupPointsList points={points} />}
              <PaperOrderDialog open={dialog} onOpenChange={setDialog} points={points} paper={paper} digital={digital} refs={refs} common={common} />
            </div>
          )}
          {legacy && (
            <OrderLink {...common} version={legacy} orderRef={refs.livre} testid="extract-whatsapp-cta-livre" className={btnCls}>
              <MessageCircle size={20} className="flex-shrink-0" />
              <span className="flex flex-col leading-tight"><span className="text-base">{legacy.label}</span>{legacy.price && <span className="text-xs text-[#FAF9F6]/75 mt-1">{legacy.price}</span>}</span>
            </OrderLink>
          )}
        </div>
      )}
      <DataSaverBlock />
      <div className="space-y-3 max-w-md mx-auto mt-8">
        {extract.secondary_cta_label && extract.secondary_cta_url && (
          <SmartLink url={extract.secondary_cta_url} onClick={() => onTrack("secondary")} testid="extract-secondary-cta" className={`${outlineCls} border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF9F6]`}>
            {extract.secondary_cta_label}<ArrowUpRight size={14} />
          </SmartLink>
        )}
        {sponsor?.offer_label && sponsor?.offer_url && (
          <SmartLink url={sponsor.offer_url} onClick={() => onTrack("partner_offer")} testid="extract-partner-offer-cta" className={`${outlineCls} border-[#9E4735] text-[#9E4735] hover:bg-[#9E4735] hover:text-[#FAF9F6]`}>
            <Gift size={14} />{sponsor.offer_label}
          </SmartLink>
        )}
      </div>
    </section>
  );
});
